import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import { Grid, Typography, Box } from "@mui/material";
import { CloudUpload as CloudUploadIcon } from "@mui/icons-material";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import { uploadCSV } from "../../api/settingsApi";
import CustomButton from "components/CustomButton";
import { useAuth0 } from "@auth0/auth0-react";

const UploadData = ({
  onSnackBarOpen,
  setPresetData,
  selectedPreset,
  onLoading,
  selectedStore,
  setShowNotification,
}) => {
  const [selectedFile, setSelectedFile] = useState(null);
  const [isDragOver, setIsDragOver] = useState(false);
  const { user } = useAuth0();

  useEffect(() => {
    if (selectedPreset) {
      setSelectedFile(null);
    }
  }, [selectedPreset]);

  const handleFileChange = (event) => {
    const file = event.target.files[0];
    setSelectedFile(file);
  };

  const handleDragOver = (event) => {
    event.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = (event) => {
    event.preventDefault();
    setIsDragOver(false);
  };

  const handleDrop = (event) => {
    event.preventDefault();
    setIsDragOver(false);
    const file = event.dataTransfer.files[0];
    setSelectedFile(file);
  };

  const handleUploadCSV = async () => {
    if (!selectedStore) {
      setShowNotification(true);
      return;
    }
    onLoading(true);

    try {
      let formData;

      if (selectedFile) {
        formData = new FormData();
        formData.append("csvFile", selectedFile);
        formData.append("storeId", selectedStore);
        formData.append("authId", user.sub); // Include the user ID (Auth0 ID)
      } else if (selectedPreset) {
        const response = await fetch(`${process.env.PUBLIC_URL}/${selectedPreset}`);
        const blob = await response.blob();
        formData = new FormData();
        formData.append("csvFile", blob, selectedPreset);
        formData.append("storeId", selectedStore);
        formData.append("authId", user.sub); // Include the user ID (Auth0 ID)
      }

      if (formData) {
        await uploadCSV(formData);
        onSnackBarOpen("success", "CSV uploaded successfully!");
        setPresetData(null);
      }
    } catch (error) {
      console.error("Error:", error);
      onSnackBarOpen("error", "Failed to upload CSV.");
    } finally {
      onLoading(false);
    }
  };

  return (
    <Grid item xs={6}>
      <MDTypography variant="h6" gutterBottom>
        Upload Data
      </MDTypography>
      <MDBox
        display="flex"
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
        border={isDragOver ? "2px dashed" : "2px solid"}
        borderColor={isDragOver ? "primary.main" : "divider"}
        borderRadius="4px"
        p={2}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        <CloudUploadIcon fontSize="large" color="action" />
        <Typography variant="body1" align="center" gutterBottom>
          {selectedFile ? selectedFile.name : "Drag and drop a CSV file here or click to select"}
        </Typography>
        <CustomButton
          variant="contained"
          component="label"
          color="info"
          size="medium"
          sx={{ mt: 2 }}
        >
          Select File
          <input type="file" hidden onChange={handleFileChange} accept=".csv" />
        </CustomButton>
      </MDBox>
      <MDBox mt={2}>
        <CustomButton
          variant="contained"
          color="info"
          size="medium"
          onClick={handleUploadCSV}
          disabled={!selectedFile && !selectedPreset}
          sx={{ mt: 2 }}
        >
          Upload Data
        </CustomButton>
      </MDBox>
    </Grid>
  );
};

UploadData.propTypes = {
  onSnackBarOpen: PropTypes.func.isRequired,
  setPresetData: PropTypes.func.isRequired,
  selectedPreset: PropTypes.string,
  onLoading: PropTypes.func.isRequired,
  selectedStore: PropTypes.string.isRequired,
  setShowNotification: PropTypes.func.isRequired,
};

export default UploadData;
